import { ElementRef, Injectable, NgZone, Optional, DestroyRef, WritableSignal, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

export enum ShortcutKey {
	SLASH = '/',
	QUESTION_MARK = '?',
	ARROW_DOWN = 'ArrowDown',
	ARROW_UP = 'ArrowUp',
	ENTER = 'Enter',
	SPACE = ' ',
	/** Escape key used to close dialogs, drawer panels, or clear focus */
	ESCAPE = 'Escape',
	/** Tab key used for sequential focus navigation */
	TAB = 'Tab'
}

export enum ShortcutCode {
	SLASH = 'Slash',
	SPACE = 'Space',
	/** Escape key code */
	ESCAPE = 'Escape',
	/** Tab key code */
	TAB = 'Tab'
}

export enum ShortcutCodePrefix {
	KEY = 'Key',
	DIGIT = 'Digit'
}

export enum ShortcutKeyCode {
	QUESTION_MARK = 63,
	SLASH = 191
}

export interface KeyboardShortcutConfig<T = any> {
	/** Optional identifier for the page or component */
	id?: string;
	/** Function returning the items list for list navigation */
	items?: () => T[] | undefined | null;
	/** WritableSignal for the focused index, or getter/setter below */
	focusedIndex?: WritableSignal<number>;
	/** Getter for the current focused index */
	getFocusedIndex?: () => number;
	/** Setter for the focused index */
	setFocusedIndex?: (index: number) => void;
	/** Optional getter for previously selected item index */
	getSelectedIndex?: () => number;
	/** Callback invoked when Enter or Spacebar is pressed on the focused item */
	onSelect?: (item: T, index: number) => void;
	/** Optional getter for the search input element or ElementRef */
	getSearchInput?: () => HTMLInputElement | ElementRef<HTMLInputElement> | null | undefined;
	/** Optional ID of the search input element */
	searchInputId?: string;
	/** Optional CSS selector of the list container (e.g. '.systemoperations-wrapper__nav__ccuList') */
	listContainerSelector?: string;
	/** Optional function to extract display/search text from an item for filtering */
	getItemSearchText?: (item: T) => string;
	/** Optional predicate to check if keyboard navigation should be active */
	isEnabled?: () => boolean;
	/** Map of custom page-specific key shortcuts (e.g. { '+': () => zoomIn(), 'ctrl+s': () => save() }) */
	customShortcuts?: Record<string, (event: KeyboardEvent) => void>;
	/** Optional callback to open keyboard shortcuts dialog (triggered by '?' or 'shift+/') */
	onOpenShortcuts?: (event: KeyboardEvent) => void;
	/**
	 * Optional function to reveal the search input before focusing it.
	 * Useful when the search input is conditionally rendered (e.g., after a toggle/click).
	 */
	toggleHiddenSearchInput?: () => void;
}

@Injectable({
	providedIn: 'root'
})
export class KeyboardShortcutService {
	/** Signal tracking the currently focused index */
	readonly focusedIndex = signal<number>(-1);

	/** Global shortcuts map active across the entire application */
	private globalShortcuts = new Map<string, (event: KeyboardEvent) => void>();

	/** Global opener callback for keyboard shortcuts dialog */
	private globalOnOpenShortcuts?: (event: KeyboardEvent) => void;

	/** Currently active page-specific context */
	private activeConfig?: KeyboardShortcutConfig<any>;

	constructor(
		private ngZone: NgZone,
		@Optional() private dialog?: MatDialog
	) {
		this.initGlobalListener();
	}

	/**
	 * Initializes the single global keydown listener outside Angular Zone.
	 */
	private initGlobalListener(): void {
		this.ngZone.runOutsideAngular(() => {
			document.addEventListener('keydown', (event: KeyboardEvent) => {
				this.handleGlobalKeydown(event);
			});
		});
	}

	/**
	 * Registers a global shortcut active across all pages (e.g. 'Shift+?', 'Alt+z', 'Ctrl+k').
	 * Can accept a key combination string and callback, OR a global configuration object.
	 */
	registerGlobal(
		keyComboOrConfig: string | { onOpenShortcuts?: (event: KeyboardEvent) => void; [key: string]: any },
		action?: (event: KeyboardEvent) => void
	): () => void {
		if (typeof keyComboOrConfig === 'string' && action) {
			const keyCombo = keyComboOrConfig;
			const normalizedCombo = this.normalizeKeyCombo(keyCombo);
			this.globalShortcuts.set(normalizedCombo, action);

			// If registering '?', 'shift+?' or 'shift+/', register all aliases
			if (normalizedCombo === '?' || normalizedCombo === 'shift+?' || normalizedCombo === 'shift+/') {
				this.globalShortcuts.set('?', action);
				this.globalShortcuts.set('shift+?', action);
				this.globalShortcuts.set('shift+/', action);
				this.globalOnOpenShortcuts = action;
			}

			return () => {
				this.unregisterGlobal(keyCombo);
			};
		} else if (typeof keyComboOrConfig === 'object') {
			if (keyComboOrConfig.onOpenShortcuts) {
				const openFn = keyComboOrConfig.onOpenShortcuts;
				this.globalOnOpenShortcuts = openFn;
				this.globalShortcuts.set('?', openFn);
				this.globalShortcuts.set('shift+?', openFn);
				this.globalShortcuts.set('shift+/', openFn);
			}
			return () => {
				if (keyComboOrConfig.onOpenShortcuts && this.globalOnOpenShortcuts === keyComboOrConfig.onOpenShortcuts) {
					this.globalOnOpenShortcuts = undefined;
					this.globalShortcuts.delete('?');
					this.globalShortcuts.delete('shift+?');
					this.globalShortcuts.delete('shift+/');
				}
			};
		}
		return () => {};
	}

	/**
	 * Unregisters a previously registered global shortcut.
	 */
	unregisterGlobal(keyCombo: string): void {
		const normalizedCombo = this.normalizeKeyCombo(keyCombo);
		this.globalShortcuts.delete(normalizedCombo);
		if (normalizedCombo === '?' || normalizedCombo === 'shift+?' || normalizedCombo === 'shift+/') {
			this.globalShortcuts.delete('?');
			this.globalShortcuts.delete('shift+?');
			this.globalShortcuts.delete('shift+/');
			this.globalOnOpenShortcuts = undefined;
		}
	}

	/**
	 * Registers a page-specific shortcut configuration (sets active context).
	 * Automatically resets when the component is destroyed.
	 */
	register<T = any>(config: KeyboardShortcutConfig<T>, destroyRef?: DestroyRef): () => void {
		this.activeConfig = config;
		if (config.onOpenShortcuts) {
			this.globalOnOpenShortcuts = config.onOpenShortcuts;
		}

		const unregister = () => {
			if (this.activeConfig === config) {
				this.activeConfig = undefined;
				this.resetFocus(config);
			}
		};

		if (destroyRef) {
			destroyRef.onDestroy(unregister);
		}

		return unregister;
	}

	/**
	 * Alias for register() with explicit page context semantics.
	 */
	registerPageContext<T = any>(config: KeyboardShortcutConfig<T>, destroyRef?: DestroyRef): () => void {
		return this.register(config, destroyRef);
	}

	/**
	 * Central dispatcher handling every keydown event in the application.
	 */
	private handleGlobalKeydown(event: KeyboardEvent): void {
		// Prevent handling keyboard shortcuts if a modal dialog is open
		if (this.isDialogOpen()) {
			return;
		}

		const keyCombo = this.getKeyCombo(event);
		const target = event.target as HTMLElement;
		const activeEl = document.activeElement as HTMLElement;

		const isEditable = this.isInputElement(target) || this.isInputElement(activeEl);

		const searchEl = this.activeConfig ? this.resolveSearchInput(this.activeConfig) : null;
		const isSearchInput = this.isSearchInputElement(target, searchEl, this.activeConfig) ||
			this.isSearchInputElement(activeEl, searchEl, this.activeConfig);

		// Check for '?' key (Shift + /) to open Keyboard Shortcuts popup dialog
		const isQuestionMark = event.key === ShortcutKey.QUESTION_MARK ||
			event.keyCode === ShortcutKeyCode.QUESTION_MARK || event.which === ShortcutKeyCode.QUESTION_MARK ||
			(event.shiftKey && (event.key === ShortcutKey.SLASH || event.code === ShortcutCode.SLASH ||
				event.keyCode === ShortcutKeyCode.SLASH || event.which === ShortcutKeyCode.SLASH));

		if (isQuestionMark && !isEditable) {
			const action = this.globalShortcuts.get('?') ||
				this.globalShortcuts.get('shift+?') ||
				this.globalShortcuts.get('shift+/') ||
				this.activeConfig?.onOpenShortcuts ||
				this.globalOnOpenShortcuts ||
				(this.activeConfig?.customShortcuts ? this.matchCustomShortcut(this.activeConfig.customShortcuts, '?', event) : undefined);

			event.preventDefault();
			if (action) {
				this.ngZone.run(() => action(event));
			}
			return;
		}

		// 1. Check active page's custom shortcuts first (highest priority)
		if (this.activeConfig?.customShortcuts && !isEditable) {
			const customAction = this.matchCustomShortcut(this.activeConfig.customShortcuts, keyCombo, event);
			if (customAction) {
				event.preventDefault();
				this.ngZone.run(() => customAction(event));
				return;
			}
		}

		// 2. Check active page's list navigation (Zones, System Operations, etc.)
		if (this.activeConfig && this.activeConfig.items) {
			const handled = this.handleListNavigation(event, this.activeConfig, isSearchInput, isEditable, searchEl);
			if (handled) {
				return;
			}
		} else if (this.activeConfig && (this.activeConfig.getSearchInput || this.activeConfig.searchInputId)) {
			// Pages with just only search field and no list (Scheduling, Shared Schedule, etc.)
			const isSlash = event.key === ShortcutKey.SLASH || event.code === ShortcutCode.SLASH;
			if (isSlash && !isEditable && !isSearchInput && searchEl) {
				event.preventDefault();
				this.stopNextKeyupEvent();
				this.ngZone.run(() => {
					searchEl.focus();
					searchEl.select();
				});
				return;
			}

			// If the search input is not initially rendered (i.e. not present in the DOM initially but will be revealed upon using toggle button), reveal it using the toggleHiddenSearchInput function.
			if (isSlash && !isEditable && !isSearchInput && !searchEl && this.activeConfig.toggleHiddenSearchInput) {
				event.preventDefault();
				this.ngZone.run(() => {
					this.activeConfig!.toggleHiddenSearchInput!();
					setTimeout(() => {
						const revealedEl = this.resolveSearchInput(this.activeConfig!);
						if (revealedEl) {
							this.stopNextKeyupEvent();
							revealedEl.focus();
							revealedEl.select();
						}
					});
				});
				return;
			}
		}

		// 3. Check global shortcuts (available everywhere when not typing in form inputs)
		if (!isEditable) {
			const globalAction = this.globalShortcuts.get(keyCombo) ||
				(isQuestionMark ? this.globalShortcuts.get('?') : undefined);
			if (globalAction) {
				event.preventDefault();
				this.ngZone.run(() => globalAction(event));
				return;
			}
		}
	}

	/**
	 * Handles standard list navigation for pages with a searchable item list.
	 */
	private handleListNavigation<T>(
		event: KeyboardEvent,
		config: KeyboardShortcutConfig<T>,
		isSearchInput: boolean,
		isEditable: boolean,
		searchEl: HTMLInputElement | null
	): boolean {
		const items = config.items ? config.items() : null;
		const isEnabled = config.isEnabled ? config.isEnabled() : !!(items && items.length > 0);
		if (!isEnabled || !items || items.length === 0) {
			return false;
		}

		const key = event.key;
		const code = event.code;
		const isSlash = key === ShortcutKey.SLASH || code === ShortcutCode.SLASH;
		const isArrowDown = key === ShortcutKey.ARROW_DOWN;
		const isArrowUp = key === ShortcutKey.ARROW_UP;
		const isEnter = key === ShortcutKey.ENTER;
		const isSpace = key === ShortcutKey.SPACE || code === ShortcutCode.SPACE;

		if (!isSlash && !isArrowDown && !isArrowUp && !isEnter && !isSpace) {
			return false;
		}

		// '/' shortcut to focus search bar
		if (isSlash) {
			if (!isEditable && !isSearchInput) {
				event.preventDefault();
				this.stopNextKeyupEvent();
				this.ngZone.run(() => {
					if (searchEl) {
						searchEl.focus();
						searchEl.select();
					}
				});
				return true;
			}
			return false;
		}

		// Don't hijack keys when user is typing in form inputs or search input
		if (isEditable || isSearchInput) {
			return false;
		}

		// Navigate Up / Down
		if (isArrowDown) {
			event.preventDefault();
			this.ngZone.run(() => {
				this.navigateList('down', config);
			});
			return true;
		}

		if (isArrowUp) {
			event.preventDefault();
			this.ngZone.run(() => {
				this.navigateList('up', config);
			});
			return true;
		}

		// Select with Enter or Spacebar
		if (isEnter || isSpace) {
			event.preventDefault();
			this.ngZone.run(() => {
				this.selectFocused(config);
			});
			return true;
		}

		return false;
	}

	/**
	 * Public entry point for triggering keydown (useful for testing or direct dispatch).
	 */
	handleKeydown<T>(event: KeyboardEvent, config?: KeyboardShortcutConfig<T>): void {
		if (config) {
			this.activeConfig = config;
		}
		this.handleGlobalKeydown(event);
	}

	/**
	 * Navigates the list up or down among visible items.
	 */
	navigateList<T>(direction: 'up' | 'down', config?: KeyboardShortcutConfig<T>): void {
		const targetConfig = config || this.activeConfig;
		if (!targetConfig) {
			return;
		}

		const visibleIndices = this.getVisibleIndices(targetConfig);
		if (visibleIndices.length === 0) {
			return;
		}

		let currentIndex = this.getFocusedIndex(targetConfig);

		// If nothing is focused yet, check if there is a previously selected item
		if (currentIndex === -1 && targetConfig.getSelectedIndex) {
			const selectedIdx = targetConfig.getSelectedIndex();
			if (selectedIdx !== undefined && selectedIdx !== null && selectedIdx >= 0) {
				currentIndex = selectedIdx;
			}
		}

		let currentVisiblePos = visibleIndices.indexOf(currentIndex);

		if (currentVisiblePos === -1) {
			currentVisiblePos = direction === 'down' ? 0 : visibleIndices.length - 1;
		} else {
			if (direction === 'down') {
				currentVisiblePos = currentVisiblePos < visibleIndices.length - 1 ? currentVisiblePos + 1 : currentVisiblePos;
			} else {
				currentVisiblePos = currentVisiblePos > 0 ? currentVisiblePos - 1 : 0;
			}
		}

		const newIndex = visibleIndices[currentVisiblePos];
		this.setFocusedIndex(targetConfig, newIndex);
		this.scrollIntoView(newIndex, targetConfig.listContainerSelector);
	}

	/**
	 * Selects the currently focused item.
	 */
	selectFocused<T>(config?: KeyboardShortcutConfig<T>): void {
		const targetConfig = config || this.activeConfig;
		if (!targetConfig || !targetConfig.items || !targetConfig.onSelect) {
			return;
		}

		const items = targetConfig.items();
		const currentIndex = this.getFocusedIndex(targetConfig);
		if (items && currentIndex >= 0 && currentIndex < items.length) {
			const targetItem = items[currentIndex];
			if (targetItem) {
				targetConfig.onSelect(targetItem, currentIndex);
			}
		}
	}

	/**
	 * Returns visible indices based on current search input value.
	 */
	getVisibleIndices<T>(config?: KeyboardShortcutConfig<T>): number[] {
		const targetConfig = config || this.activeConfig;
		if (!targetConfig || !targetConfig.items) {
			return [];
		}

		const items = targetConfig.items();
		if (!items || items.length === 0) {
			return [];
		}

		const searchEl = this.resolveSearchInput(targetConfig);
		const searchVal = searchEl?.value?.trim()?.toUpperCase() || '';
		if (!searchVal || !targetConfig.getItemSearchText) {
			return items.map((_, index) => index);
		}

		const visibleIndices: number[] = [];
		items.forEach((item, index) => {
			const text = targetConfig.getItemSearchText!(item)?.toUpperCase() || '';
			if (text.indexOf(searchVal) > -1) {
				visibleIndices.push(index);
			}
		});
		return visibleIndices;
	}

	/**
	 * Scrolls the focused element into view smoothly.
	 */
	scrollIntoView(index: number, containerSelector?: string): void {
		if (!containerSelector) {
			return;
		}
		setTimeout(() => {
			const cleanSelector = containerSelector.startsWith('.') ? containerSelector.substring(1) : containerSelector;
			const ulElements = document.getElementsByClassName(cleanSelector);
			if (ulElements && ulElements.length > 0) {
				const liElements = ulElements[0].querySelectorAll('li');
				if (liElements && liElements[index]) {
					liElements[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
				}
			}
		}, 0);
	}

	/**
	 * Synchronizes the focused index when search query changes.
	 */
	syncFocusOnSearch<T>(config?: KeyboardShortcutConfig<T>): void {
		const targetConfig = config || this.activeConfig;
		if (!targetConfig) {
			return;
		}

		const currentIndex = this.getFocusedIndex(targetConfig);
		if (currentIndex !== -1) {
			const visibleIndices = this.getVisibleIndices(targetConfig);
			if (visibleIndices.length > 0 && !visibleIndices.includes(currentIndex)) {
				this.setFocusedIndex(targetConfig, visibleIndices[0]);
			} else if (visibleIndices.length === 0) {
				this.setFocusedIndex(targetConfig, -1);
			}
		}
	}

	/**
	 * Resets the focused index to -1.
	 */
	resetFocus<T>(config?: KeyboardShortcutConfig<T>): void {
		const targetConfig = config || this.activeConfig;
		if (targetConfig) {
			this.setFocusedIndex(targetConfig, -1);
		} else {
			this.focusedIndex.set(-1);
		}
	}

	/**
	 * Extracts a normalized key combo string from a KeyboardEvent (e.g. 'control+s', 'alt+z', 'shift+?').
	 */
	getKeyCombo(event: KeyboardEvent): string {
		const parts: string[] = [];
		if (event.ctrlKey || event.metaKey) {
			parts.push('ctrl');
		}
		if (event.altKey) {
			parts.push('alt');
		}
		if (event.shiftKey && event.key !== 'Shift') {
			parts.push('shift');
		}
		if (event.key && !['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) {
			parts.push(event.key.toLowerCase());
		}
		return parts.join('+');
	}

	/**
	 * Normalizes a user-specified key combo string (e.g. 'Ctrl+S' -> 'ctrl+s').
	 */
	private normalizeKeyCombo(combo: string): string {
		return combo
			.toLowerCase()
			.replace(/\s+/g, '')
			.replace('control+', 'ctrl+');
	}

	/**
	 * Matches a custom shortcut by checking exact normalized combo or raw key.
	 */
	private matchCustomShortcut(
		customShortcuts: Record<string, (event: KeyboardEvent) => void>,
		keyCombo: string,
		event: KeyboardEvent
	): ((event: KeyboardEvent) => void) | undefined {
		const normalizedCombo = this.normalizeKeyCombo(keyCombo);
		const rawKey = event.key.toLowerCase();

		for (const [pattern, action] of Object.entries(customShortcuts)) {
			const normalizedPattern = this.normalizeKeyCombo(pattern);
			if (normalizedPattern === normalizedCombo || normalizedPattern === rawKey) {
				return action;
			}
		}
		return undefined;
	}

	/**
	 * Gets current focused index from config or internal signal.
	 */
	private getFocusedIndex<T>(config: KeyboardShortcutConfig<T>): number {
		if (config.focusedIndex) {
			return config.focusedIndex();
		}
		if (config.getFocusedIndex) {
			return config.getFocusedIndex();
		}
		return this.focusedIndex();
	}

	/**
	 * Sets focused index in config and internal signal.
	 */
	private setFocusedIndex<T>(config: KeyboardShortcutConfig<T>, index: number): void {
		if (config.focusedIndex) {
			config.focusedIndex.set(index);
		} else if (config.setFocusedIndex) {
			config.setFocusedIndex(index);
		}
		this.focusedIndex.set(index);
	}

	/**
	 * Resolves search HTMLInputElement from config (getter or element ID).
	 */
	private resolveSearchInput<T>(config: KeyboardShortcutConfig<T>): HTMLInputElement | null {
		if (config.getSearchInput) {
			const res = config.getSearchInput();
			if (res) {
				return (res instanceof ElementRef ? res.nativeElement : res) as HTMLInputElement;
			}
		}
		if (config.searchInputId) {
			return document.getElementById(config.searchInputId) as HTMLInputElement;
		}
		return null;
	}

	/**
	 * Checks if any modal dialog is currently open.
	 */
	private isDialogOpen(): boolean {
		if (this.dialog && this.dialog.openDialogs && this.dialog.openDialogs.length > 0) {
			return true;
		}
		return !!document.querySelector('.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-dialog-container');
	}

	/**
	 * Checks if an element is an editable form input or contentEditable.
	 */
	private isInputElement(el: HTMLElement | null): boolean {
		if (!el) {
			return false;
		}
		if (el.isContentEditable) {
			return true;
		}
		const tagName = el.tagName?.toLowerCase();
		return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
	}

	/**
	 * Checks if an element is specifically a search input.
	 */
	private isSearchInputElement(
		el: HTMLElement | null,
		searchEl: HTMLInputElement | null,
		config?: KeyboardShortcutConfig
	): boolean {
		if (!el) {
			return false;
		}
		if (searchEl && (el === searchEl || searchEl.contains(el))) {
			return true;
		}
		if (config?.searchInputId && el.id === config.searchInputId) {
			return true;
		}
		const tagName = el.tagName?.toLowerCase();
		if (tagName === 'input' || tagName === 'textarea') {
			const id = el.id?.toLowerCase() || '';
			const name = (el.getAttribute('name') || '').toLowerCase();
			const placeholder = (el.getAttribute('placeholder') || '').toLowerCase();
			const className = el.className?.toString().toLowerCase() || '';
			if (id.includes('search') || name.includes('search') || placeholder.includes('search') || className.includes('search')) {
				return true;
			}
		}
		return false;
	}


	// Method to stop the next 'keyup' event from propagating to prevent empty value triggers in input fields.
	stopNextKeyupEvent() {
		const handler = (event: KeyboardEvent) => {
			event.stopImmediatePropagation();
			document.removeEventListener('keyup', handler, true);
		};
		document.addEventListener('keyup', handler, true);
	}
}
